import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileUp, X } from 'lucide-react';
import { useInvoices } from '../api/queries/invoices';
import { useRecurringInvoices } from '../api/queries/recurring-invoices';
import { ApiError, api } from '../api/client';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { BulkActionBar } from '../components/BulkActionBar';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { useRowSelection } from '../hooks/useRowSelection';
import { InvoicePdfIngest } from '../components/ingest/InvoicePdfIngest';
import { RecurringInvoicesPanel } from './RecurringInvoicesPage';
import './InvoicesPage.css';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

type InvoiceSortKey =
  | 'number'
  | 'client'
  | 'issue_date'
  | 'due_date'
  | 'subtotal'
  | 'tax'
  | 'total'
  | 'status'
  | 'paid_date';

import { formatMoney } from '../utils/money';

export function InvoicesPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const view: 'invoices' | 'rules' = viewParam === 'rules' ? 'rules' : 'invoices';
  const rowSel = useRowSelection();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importBanner, setImportBanner] = useState<string | null>(null);

  const setView = (next: 'invoices' | 'rules') => {
    if (next === 'rules') setSearchParams({ view: 'rules' }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const { data, isPending, error } = useInvoices();
  const rulesQ = useRecurringInvoices();
  const rulesCount = rulesQ.data?.recurring_invoice_templates?.length ?? 0;

  const invoices = data?.invoices ?? [];
  const sort = useClientSort(invoices, (i, key: InvoiceSortKey) => {
    switch (key) {
      case 'number': return i.invoice_number;
      case 'client': return i.client_company_name ?? i.client_name ?? `#${i.client_id}`;
      case 'issue_date': return i.issue_date;
      case 'due_date': return i.due_date;
      case 'subtotal': return Number(i.subtotal);
      case 'tax': return Number(i.tax_amount);
      case 'total': return Number(i.total);
      case 'status': return STATUS_LABEL[i.status] ?? i.status;
      case 'paid_date': return i.paid_at ?? 0;
      default: return '';
    }
  }, { key: 'issue_date', dir: 'desc' });

  // Invoices have no "approve" step — closest bulk action is mark paid
  // (API allows draft/sent/overdue; blocks paid + void).
  const markableIds = rowSel.selectedIds.filter((id) => {
    const inv = invoices.find((x) => x.id === id);
    return inv != null && inv.status !== 'paid' && inv.status !== 'void';
  });

  const bulkMarkPaid = async () => {
    if (markableIds.length === 0 || !can('invoice.mark_paid')) return;
    setBulkError(null);
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        markableIds.map((id) => api.post(`/api/invoices/${id}/mark-paid`, {})),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setBulkError(
          failed === results.length
            ? 'Could not mark the selected invoices as paid.'
            : `Marked ${results.length - failed} paid; ${failed} failed.`,
        );
      }
      rowSel.clear();
    } finally {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      setBulkBusy(false);
    }
  };

  const outstanding = invoices
    .filter((i) => i.status === 'sent' || i.status === 'overdue')
    .reduce((acc, i) => acc + Number(i.total), 0);
  const paid = invoices
    .filter((i) => i.status === 'paid')
    .reduce((acc, i) => acc + Number(i.total), 0);
  const draft = invoices
    .filter((i) => i.status === 'draft')
    .reduce((acc, i) => acc + Number(i.total), 0);

  return (
    <section className="page-stack page-stack-wide">
      <div className="card iv-toolbar">
        <header className="iv-header">
          <div className="iv-title-group">
            <h2>Invoices</h2>
            <div className="iv-viewtoggle" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'invoices'}
                className={view === 'invoices' ? 'iv-viewtoggle-btn is-active' : 'iv-viewtoggle-btn'}
                onClick={() => setView('invoices')}
              >
                Invoices
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'rules'}
                className={view === 'rules' ? 'iv-viewtoggle-btn is-active' : 'iv-viewtoggle-btn'}
                onClick={() => setView('rules')}
              >
                Repeating rules{rulesCount > 0 ? ` (${rulesCount})` : ''}
              </button>
            </div>
          </div>
          {view === 'invoices' && (
            <div className="iv-header-actions">
              <span className="iv-count">{invoices.length} invoice(s)</span>
              {can('invoice.write') && (
                <>
                  <button
                    type="button"
                    className={`icon-btn${showImport ? ' icon-btn-cyan' : ''}`}
                    title={showImport ? 'Close import' : 'Import invoice PDFs'}
                    aria-label={showImport ? 'Close import' : 'Import invoice PDFs'}
                    aria-expanded={showImport}
                    onClick={() => {
                      setShowImport((v) => !v);
                      setImportBanner(null);
                    }}
                  >
                    {showImport
                      ? <X size={18} strokeWidth={1.75} aria-hidden />
                      : <FileUp size={18} strokeWidth={1.75} aria-hidden />}
                  </button>
                  <Link to="/invoices/new" className="btn-cyan-primary">
                    New invoice
                  </Link>
                </>
              )}
            </div>
          )}
        </header>
      </div>

      {view === 'invoices' && showImport && can('invoice.write') && (
        <div className="card eli-panel">
          <header className="eli-panel-head">
            <h3>Import Invoice PDFs</h3>
            <span className="eli-panel-meta">Up to 10 files · review before saving</span>
          </header>
          <InvoicePdfIngest
            onCancel={() => setShowImport(false)}
            onApplied={(n) => {
              setShowImport(false);
              setImportBanner(
                n === 1 ? 'Imported 1 invoice.' : `Imported ${n} invoices.`,
              );
              void qc.invalidateQueries({ queryKey: ['invoices'] });
            }}
          />
        </div>
      )}

      {view === 'invoices' && importBanner && (
        <div className="card">
          <p className="iv-status" role="status">{importBanner}</p>
        </div>
      )}

      {view === 'rules' ? (
        <RecurringInvoicesPanel />
      ) : isPending ? (
        <div className="card"><p className="iv-status">Loading invoices…</p></div>
      ) : error ? (
        <div className="card">
          <p className="iv-status iv-error">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view invoices."
              : 'Failed to load invoices.'}
          </p>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="iv-summary">
              <div className="iv-summary-card">
                <div className="iv-summary-label">Outstanding</div>
                <div className="iv-summary-value iv-summary-warn">
                  {formatMoney(outstanding.toFixed(2))}
                </div>
              </div>
              <div className="iv-summary-card">
                <div className="iv-summary-label">Drafts</div>
                <div className="iv-summary-value">{formatMoney(draft.toFixed(2))}</div>
              </div>
              <div className="iv-summary-card">
                <div className="iv-summary-label">Paid (visible)</div>
                <div className="iv-summary-value iv-summary-success">
                  {formatMoney(paid.toFixed(2))}
                </div>
              </div>
            </div>
          </div>

          {invoices.length === 0 ? (
            <div className="card">
              <div className="iv-empty">
                <p>No invoices yet.</p>
              </div>
            </div>
          ) : (
            <>
              {bulkError && (
                <div className="card"><p className="iv-error" role="alert">{bulkError}</p></div>
              )}
              <BulkActionBar
                count={rowSel.count}
                onClear={rowSel.clear}
                busy={bulkBusy}
                hint={
                  can('invoice.mark_paid')
                    ? (markableIds.length > 0
                      ? `${markableIds.length} ready to mark paid`
                      : 'None of the selected can be marked paid')
                    : undefined
                }
              >
                {can('invoice.mark_paid') && (
                  <button
                    type="button"
                    className="btn-cyan-primary"
                    disabled={bulkBusy || markableIds.length === 0}
                    onClick={() => void bulkMarkPaid()}
                  >
                    {bulkBusy
                      ? 'Marking paid…'
                      : `Mark paid${markableIds.length > 0 ? ` (${markableIds.length})` : ''}`}
                  </button>
                )}
              </BulkActionBar>
            <div className="card card-tight iv-table-card table-scroll-card">
              <p className="iv-table-hint">
                Click a row to open. Hold <kbd>Ctrl</kbd> (Mac: <kbd>⌘</kbd>) and click to multi-select for bulk mark paid.
              </p>
              <TableScroll id="invoices">
                <table className="iv-table">
                  <thead>
                    <tr>
                      <SortableTh column="number" label="Number" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                      <SortableTh column="client" label="Client" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                      <SortableTh column="issue_date" label="Issue date" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                      <SortableTh column="due_date" label="Due date" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                      <SortableTh column="subtotal" label="Subtotal" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="iv-numeric" />
                      <SortableTh column="tax" label="Tax" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="iv-numeric" />
                      <SortableTh column="total" label="Total" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="iv-numeric" />
                      <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                      <SortableTh column="paid_date" label="Paid date" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {sort.sorted.map((i) => (
                      <tr
                        key={i.id}
                        className={`iv-row${rowSel.isSelected(i.id) ? ' table-row-selected' : ''}`}
                        aria-selected={rowSel.isSelected(i.id)}
                        onMouseDown={rowSel.suppressTextSelect}
                        onClick={(ev) => {
                          const ordered = sort.sorted.map((x) => x.id);
                          if (rowSel.handleRowClick(ev, i.id, ordered)) return;
                          navigate(`/invoices/${i.id}`);
                        }}
                      >
                        <td>
                          <code className="iv-number">{i.invoice_number}</code>
                          {i.matter_name && <div className="iv-matter">{i.matter_name}</div>}
                        </td>
                        <td>{i.client_company_name ?? i.client_name ?? `#${i.client_id}`}</td>
                        <td>{fmtDate(i.issue_date)}</td>
                        <td>{fmtDate(i.due_date)}</td>
                        <td className="iv-numeric">{formatMoney(i.subtotal)}</td>
                        <td className="iv-numeric">{formatMoney(i.tax_amount)}</td>
                        <td className="iv-numeric iv-total">{formatMoney(i.total)}</td>
                        <td>
                          <span className={`iv-pill iv-status-${i.status}`}>
                            {STATUS_LABEL[i.status] ?? i.status}
                          </span>
                        </td>
                        <td>{i.paid_at ? fmtDate(i.paid_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
